import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
}                              from '@angular/core';
import {PDFExportComponent}    from '@progress/kendo-angular-pdf-export';
import * as _                  from 'lodash';
import {
  LanguageStore
}                              from '../../../stores/translation/language.store';
import {
  ApDateService
}                              from '../../../ap-core/services/ap-date-service';
import {LoginStore}            from '../../../stores/login/login.store';
import {
  CropTypeStore
}                              from '../../../stores/base-data/crop.types.store';
import {
  SettingsStore
}                              from '../../../stores/base-data/settings.store';
import {
  GetRoundNumericService
}                              from '../../../ap-utils/service/get-round-numeric.service';
import {MapStore}              from '../../../stores/map/map.store';
import {ApBaseVectorLayer}     from '../../../ap-map';
import OlStyle                 from 'ol/style/Style';
import OlStyleStroke           from 'ol/style/Stroke';
import OlStyleFill             from 'ol/style/Fill';
import OlStyleText             from 'ol/style/Text';
import {
  TaskManagementDataProviders
}                              from '../../../task-management/task-management-data-providers/task-management-data-providers';
import {ApStyleData}           from '../../../map-factory/ap-style-data';
import {MapFactoryStyler}      from '../../../map-factory/style';
import {Subscription}          from 'rxjs';
import {asString}              from 'ol/color';
import {exportPDF}             from '@progress/kendo-drawing';
import {saveAs}                from '@progress/kendo-file-saver';
import {PDFOptions}            from '@progress/kendo-drawing/pdf';
import {
  FertilizerStore
}                              from '../../../stores/base-data/fertilizer.store';
import {
  TranslationStore
}                              from '../../../stores/translation/translation.store';
import {
  FactorService
}                              from '../../../ap-utils/service/ap-factor.service';
import {ApElementOxydTypeEnum} from '../../../ap-interface';
import {FieldStore}            from '../../../stores/farm/field.store';
import {
  ApApplicationMode
}                              from '../../../ap-interface/enums/ap-application-mode.enums';
import {
  ITaskManagementPlanBookGridData,
  ITaskManagementPlanSummaryGridData
}                              from '../../../task-management/task-management.types';
import {
  PpCommonService
}                              from '../../../plant-protection/services/pp-common.service';
import IFarm = Data.Authentication.IFarm;
import IMachine = Data.DocuContext.Machine.IMachine;
import IInstrument = Data.DocuContext.Instrument.IInstrument;
import IDriver = Data.DocuContext.Driver.IDriver;
import TaskManagementModule = Data.TaskManagement.TaskManagementModule;
import {TextProcessorService}  from '../../../services/common/text-processor.service';
import {TaskManagementConstants} from '../../../task-management/task-management.constants';
import {TaskManagementStore} from '../../../stores/task-management/task-management.store';

@Component({
  selector: 'task-management-report',
  templateUrl: 'task-management-report.component.html',
  styleUrls: ['task-management-report.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TaskManagementReportComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('report', {static: true}) private _report: PDFExportComponent;
  @Input() planSummary: ITaskManagementPlanSummaryGridData;
  @Input() items: ITaskManagementPlanBookGridData[] = [];
  @Output() exportDone = new EventEmitter<any>(true);
  public reportName = '';
  public firstItemFromPlans: ITaskManagementPlanBookGridData;
  public itemsChunked: ITaskManagementPlanBookGridData[][] = [];
  public currentDateTime: string;
  public currentFarm: IFarm;
  public taskManagementLayer: ApBaseVectorLayer;
  public digitsAfterDecimal: number;

  private readonly fieldsFirstPageCount = 3;
  private readonly fieldsPerPageCount = 5;
  private readonly color = [0, 0, 0, 1];
  private exportDate = new Date();
  private _subscriptions: Subscription[] = [];
  private drivers: IDriver[];
  private machines: IMachine[];
  private instruments: IInstrument[];
  public renderedMaps = 0;
  private isSaving = false;

  public readonly exportMapStyle = new OlStyle({
    stroke: new OlStyleStroke({
      color: asString(this.color),
      width: 2,
      lineCap: 'round'
    }),
    fill: new OlStyleFill({
      color: asString([...this.color.FindAll((c, idx) => idx < 3), 0.5]),
    }),
    text: new OlStyleText({
      text: ''
    })
  });

  public constructor(private mapStore: MapStore,
                     private languageStore: LanguageStore,
                     private loginStore: LoginStore,
                     private dataProviders: TaskManagementDataProviders,
                     private dateService: ApDateService,
                     private cropTypeStore: CropTypeStore,
                     private settingsStore: SettingsStore,
                     private taskManagementStore: TaskManagementStore,
                     private roundNumericService: GetRoundNumericService,
                     private translationStore: TranslationStore,
                     private fertilizerStore: FertilizerStore,
                     private factorService: FactorService,
                     private fieldStore: FieldStore,
                     private textProcessorService: TextProcessorService,
                     private ppCommonService: PpCommonService,
                     private changeDetection: ChangeDetectorRef) {
  }

  ngOnInit(): void {
    this.digitsAfterDecimal = this.settingsStore.FirstSetting.DigitsAfterDecimalPoint;
    this.currentFarm = this.loginStore.Listen(l => l.farm).getValue().selectedFarm;
    const selectedLanguage = this.languageStore.Listen(l => l.selectedLanguage).getValue();
    this.currentDateTime = this.dateService.toFarmDate(this.exportDate).format(selectedLanguage.DateFormat + ' ' + selectedLanguage.TimeFormat);
    this.taskManagementLayer = this.mapStore.Layers.TaskManagementLayer;
    this._subscriptions.push(this.dataProviders.drivers$.subscribe(d =>
      this.drivers = d
    ));
    this._subscriptions.push(this.dataProviders.machines$.subscribe(d =>
      this.machines = d
    ));
    this._subscriptions.push(this.dataProviders.instruments$.subscribe(d =>
      this.instruments = d
    ));
    // Whenever taskmanagement is active => the styles of the map factory are modified a tiny bit => no text is shown
    // unsubscription is really important here in order to not change the style when another component is activated.
    this._subscriptions.push(MapFactoryStyler.Instance.customStyle.subscribe((customStyle) =>
      this.onCustomTaskMgmtMapStyle(customStyle)));

    if (!this.items || this.items.length <= 0) {
      return;
    }

    this.items = this.items.sort((a, b) => a.SourceItem.Position - b.SourceItem.Position);
    this.firstItemFromPlans = this.items[0];
    this.itemsChunked.push(_.take(this.items, this.fieldsFirstPageCount));
    _.chunk(
      _.drop(this.items, this.fieldsFirstPageCount), this.fieldsPerPageCount)
      .forEach(c => {
        this.itemsChunked.push(c);
      });

    this.reportName = this.textProcessorService.replaceSpecialChars(
      this.dataProviders[this.planSummary?.SourceItem.Module].getExportBaseFilename(this.planSummary.SourceItem,
        this.firstItemFromPlans.SourceItem)
    );
  }

  ngAfterViewInit(): void {
    this.changeDetection.detectChanges();
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(s => s.unsubscribe());
  }

  public getModuleName(): string {
    switch (this.planSummary?.SourceItem?.Module) {
      case TaskManagementModule.N_NFertilization:
        return 'Global__NFertilization';
      case TaskManagementModule.GD_BasicFertilization:
        return 'Global__BasicFertilization';
      case TaskManagementModule.PS_Fertilization:
        return 'Global__PlantProtection';
      default:
        return '';
    }
  }

  public isNFertilization(): boolean {
    return this.planSummary?.SourceItem?.Module === TaskManagementModule.N_NFertilization;
  }

  public getFieldsCount(): number {
    return this.itemsChunked?.flat().length;
  }

  public getFieldsArea(): number {
    return this.items?.reduce((a, b) => a + b.SourceItem.AreaHa, 0);
  }

  public getApplSumForAllFields(): number {
    return this.items?.reduce((a, b) => a + (isNaN(b.ApplSum) ? 0 : b.ApplSum), 0);
  }

  public getApplRate(item: ITaskManagementPlanBookGridData): string {
    if (!item?.ApplRate) {
      return ' ';
    }
    return item.ApplRate.toString();
  }

  public getProductNutrientExpr(): string {
    let productNutrientExp = '';
    if (this.planSummary?.SourceItem?.ProductId > 0) {
      const product = this.fertilizerStore.getFertilizer(this.planSummary?.SourceItem?.ProductId);
      if (product) {
        let content = 0;
        let contentOxyd = 0;
        let oxydKey = '';
        switch (this.planSummary.SourceItem.Element?.ShortKey) {
          case 'Global__NutrientP':
            content = product.ContentP;
            contentOxyd = this.factorService.calculate(content, ApElementOxydTypeEnum.P, ApElementOxydTypeEnum.P2O5);
            oxydKey = 'Global__NutrientP2O5';
            break;
          case 'Global__NutrientK':
            content = product.ContentK;
            contentOxyd = this.factorService.calculate(content, ApElementOxydTypeEnum.K, ApElementOxydTypeEnum.K2O);
            oxydKey = 'Global__NutrientK2O';
            break;
          case 'Global__NutrientMg':
            content = product.ContentMg;
            contentOxyd = this.factorService.calculate(content, ApElementOxydTypeEnum.Mg, ApElementOxydTypeEnum.MgO);
            oxydKey = 'Global__NutrientMgO';
            break;
          case 'Global__NutrientCaO':
            content = product.ContentCaO;
            break;
          case 'Global__NutrientN':
            content = product.ContentN;
            break;
        }

        if (content > 0) {
          content = this.roundNumericService.roundAsNumber(content, 0);
          productNutrientExp = `${content}% ${this.translationStore.FindTranslationForSelectedLanguage(this.planSummary?.SourceItem?.Element?.ShortKey)}`;
        }
        if (contentOxyd > 0 && oxydKey !== '') {
          contentOxyd = this.roundNumericService.roundAsNumber(contentOxyd, 0);
          productNutrientExp += `, ${contentOxyd}% ${this.translationStore.FindTranslationForSelectedLanguage(oxydKey)}`;
        }
        if (productNutrientExp !== '') {
          productNutrientExp = ` (${productNutrientExp})`;
        }
      }
    }

    return productNutrientExp;
  }

  public getCropName(planBook: ITaskManagementPlanBookGridData): string {
    if (!planBook) {
      return '';
    }
    const cropId = planBook.SourceItem?.MainCropId;
    const cropTypeNames = this.cropTypeStore.Listen(s => s.cropTypesNames).getValue();

    return cropId in cropTypeNames ? cropTypeNames[cropId] : '';
  }

  public getExportDriverName(): string {
    const driver = this.drivers.Find(d => d.Id === this.firstItemFromPlans?.SourceItem?.ExportDriver?.Id);
    return driver?.ShortFullName?.length > 0 ? driver.ShortFullName : '-';
  }

  public getExportMachineInstrument(): string {
    const machine = this.machines.Find(d => d.Id === this.firstItemFromPlans?.SourceItem?.ExportMachine?.Id);
    const instrument = this.instruments.Find(d => d.Id === this.firstItemFromPlans?.SourceItem?.ExportInstrument?.Id);

    return `${machine?.NameWithSign?.length > 0 ? machine.NameWithSign : '-'} /
    ${instrument?.Name?.length > 0 ? instrument.Name : '-'}`;
  }

  public isApplModeVariable(): boolean {
    return this.planSummary?.SourceItem?.ApplicationMode?.Id === ApApplicationMode.Variable;
  }

  public onRenderCompleted(): void {
    this.renderedMaps++;
    if (this.renderedMaps === this.getFieldsCount()) {
      this.saveReport();
    }
  }

  private saveReport(): void {
    // Lock for being executed twice.
    // Double execution might happen because render complete of Open layers
    // might get triggered again in rare cases.
    if (this.isSaving) {
      return;
    }
    this.isSaving = true;

    // do not use _report.saveAs() here because it works async, and we never know when its finished and
    // when to close the wizard
    this._report.export().then(group => {
      const options = {
        paperSize: this._report.paperSize,
        landscape: this._report.landscape,
        margin: this._report.margin
      } as PDFOptions;
      exportPDF(group, options).then(data => {
        if (this.firstItemFromPlans.SourceItem.ExportFormat.Key === TaskManagementConstants.EXPORT_FORMAT_PDF &&
          this.firstItemFromPlans.SelectedExportTargetObject.Key !== TaskManagementConstants.EXPORT_TARGET_DOWNLOAD) {
          this.taskManagementStore.sendReport(this.firstItemFromPlans.SourceItem, data, this.firstItemFromPlans.SelectedExportTargetObject.Key);
        } else {
          // pdf generated => saving it and closing wizard
          saveAs(data, `${this.reportName}.pdf`);
        }

        setTimeout(() => this.exportDone?.emit(), 0); // make sure it`s on top of the callstack and form is only closed when pdf is generated
      });
    });
  }

  public getMapUrl(item: ITaskManagementPlanBookGridData): string {
    if (!item) {
      return '';
    }
    return `${this.dataProviders[item.SourceItem.Module].getMapFactoryUrl([item.SourceItem])}`;
  }

  /**
   * Customization of MapFactoryLayer 'TaskManagementNutrientLayer'
   */
  private onCustomTaskMgmtMapStyle(styleData: ApStyleData): void {
    if (!this.items || this.items.length < 1 || !styleData) {
      return;
    }

    // Custom Style is triggered for every MapFactoryLayer =>
    // Check if the custom style applies for current map (e.g. nutrient planning_p)
    const currentMap = this.dataProviders[this.items[0].SourceItem.Module].getMapFactoryUrl([this.items[0].SourceItem]);
    if (currentMap.indexOf(styleData.map) < 0) {
      return;
    }

    // Hide labels by cloning current style and setting text to null
    // without cloning all maps using this style would be modified => but text should be hidden
    // only in pdf
    const customStyle = styleData.style.clone();
    customStyle.setText(null);
    styleData.style = customStyle;
  }

  getFieldName(item: ITaskManagementPlanBookGridData): string {
    if (!item?.Field) {
      return '';
    }
    return this.fieldStore.getFieldName(item.Field);
  }

  public getElementExpression(): string {
    // special case for plant protection. Its not related to specific elements => returning plant protection shortcut
    if (this.planSummary?.Module === TaskManagementModule.PS_Fertilization) {
      return this.translationStore.FindTranslationForSelectedLanguage('Task_Mgmt_PS');
    }
    // for all other modules showing the element
    return this.planSummary?.ElementName;
  }

  public getProductHeader(): string {
    return this.translationStore.FindTranslationForSelectedLanguage(
      this.planSummary?.Module === TaskManagementModule.PS_Fertilization ?
        'Global__Water' :
        'Global__Product');
  }

  /**
   * Method returns a preformatted text containing the applied plant protection products
   */
  public getPpProductSumExpr(): string {
    if (this.planSummary?.Module !== TaskManagementModule.PS_Fertilization) {
      return '';
    }
    return `\n${this.ppCommonService.getAppliedProductSumExpr(
      this.items?.FirstOrDefault()?.SourceItem.PpProductList, {areaHa: this.getFieldsArea()})}`;
  }

  public isPpModule(): boolean {
    return this.planSummary?.Module === TaskManagementModule.PS_Fertilization;
  }
}
